import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Loader2, PlayCircle, RotateCcw, CheckCircle2, SkipForward, Flag, ShieldCheck,
  ExternalLink, StickyNote, AlertTriangle, Rocket, PackageCheck, History,
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const StatusBadge = ({ status }) => {
  const map = {
    reviewed: ['bg-emerald-50 text-emerald-700 border-emerald-200', 'Reviewed'],
    skipped: ['bg-amber-50 text-amber-700 border-amber-200', 'Skipped'],
    pending: ['bg-slate-100 text-slate-500 border-slate-200', 'Pending'],
  };
  const [cls, label] = map[status] || map.pending;
  return <span className={`inline-flex px-2 py-0.5 rounded-full border text-[11px] font-bold ${cls}`} data-testid={`rehearsal-step-status-${status}`}>{label}</span>;
};

// ---- Phase 51 Part 1: Rehearsal History Log (admin-only, never public) ----
const RehearsalHistory = ({ refreshKey }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get('/admin/launch-rehearsal/history').then((r) => setData(r.data)).catch(() => {});
  }, [refreshKey]);
  if (!data) return null;

  return (
    <Card className="mt-6" data-testid="rehearsal-history">
      <CardContent className="p-4 sm:p-5">
        <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><History className="h-4 w-4 text-[#C9A84C]" /> Rehearsal History</p>
        <p className="text-xs text-slate-500 mt-0.5">{data.note}</p>

        {data.summary ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3.5 mt-3" data-testid="rehearsal-readiness-summary">
            <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400 mb-2">Readiness summary</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-[11px] font-bold text-slate-400">Last rehearsal completed</p><p className="font-bold text-[#1a2744]" data-testid="summary-last-completed">{fmtDate(data.summary.last_completed)}</p></div>
              <div><p className="text-[11px] font-bold text-slate-400">Steps completed</p><p className="font-bold text-emerald-700" data-testid="summary-steps-completed">{data.summary.steps_completed}</p></div>
              <div><p className="text-[11px] font-bold text-slate-400">Steps skipped</p><p className="font-bold text-amber-700" data-testid="summary-steps-skipped">{data.summary.steps_skipped}</p></div>
              <div><p className="text-[11px] font-bold text-slate-400">Warnings</p><p className="font-bold text-[#1a2744]" data-testid="summary-warnings">{data.summary.warnings.length}</p></div>
            </div>
            <p className="text-xs text-slate-600 mt-2" data-testid="summary-audit-result"><strong>Audit result:</strong> {data.summary.audit_result || '—'}</p>
            <p className="text-[11px] text-slate-400 mt-1">Completed by {data.summary.completed_by} · {data.summary.total_rehearsals} rehearsal{data.summary.total_rehearsals === 1 ? '' : 's'} on record</p>
          </div>
        ) : (
          <p className="text-sm text-slate-500 mt-3" data-testid="rehearsal-history-empty">No rehearsals completed yet — finish a rehearsal and it will be recorded here.</p>
        )}

        {data.items.length > 0 && (
          <div className="space-y-2 mt-3">
            {data.items.map((h) => (
              <div key={h.id} className="rounded-xl border border-slate-200 p-3" data-testid={`rehearsal-history-entry-${h.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-sm font-bold text-[#1a2744]">{fmtDate(h.finished_at)}</p>
                  <span className="inline-flex px-2 py-0.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 text-[11px] font-bold">Finished</span>
                </div>
                <p className="text-xs text-slate-600 mt-1">
                  <strong className="text-emerald-700">{h.reviewed_count} reviewed</strong> · <strong className="text-amber-700">{h.skipped_count} skipped</strong> · {h.pending_count} pending · by {h.completed_by}
                </p>
                <p className="text-xs text-slate-600 mt-1"><strong>Audit:</strong> {h.audit_result}</p>
                {h.skipped_steps?.length > 0 && <p className="text-xs text-amber-700 mt-1">Skipped: {h.skipped_steps.join(', ')}</p>}
                {h.warnings?.length > 0 && <p className="text-xs text-amber-700 mt-1">{h.warnings.length} warning{h.warnings.length === 1 ? '' : 's'} recorded</p>}
                {h.notes && Object.keys(h.notes).length > 0 && (
                  <div className="mt-1.5 space-y-0.5">
                    {Object.entries(h.notes).map(([step, note]) => (
                      <p key={step} className="text-[11px] text-slate-500"><strong>{step}:</strong> {note}</p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default function AdminRehearsal() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [summary, setSummary] = useState(null);
  const [historyKey, setHistoryKey] = useState(0);

  const load = useCallback(() => {
    api.get('/admin/launch-rehearsal').then((r) => setData(r.data)).catch(() => toast.error('Failed to load rehearsal'));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!data) return <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const call = async (fn, okMsg) => {
    try {
      const r = await fn();
      if (okMsg) toast.success(okMsg);
      return r;
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); return null; }
  };

  const start = async () => {
    setBusy('start'); setSummary(null);
    const r = await call(() => api.post('/admin/launch-rehearsal/start'), 'Rehearsal started — work through each step. Dry run only.');
    if (r) setData(r.data);
    setBusy('');
  };

  const setStep = async (id, status) => {
    const r = await call(() => api.put(`/admin/launch-rehearsal/step/${id}`, { status }));
    if (r) setData(r.data);
  };

  const saveNote = async (id, note) => {
    const r = await call(() => api.put(`/admin/launch-rehearsal/step/${id}`, { note }), 'Note saved');
    if (r) setData(r.data);
  };

  const runAudit = async (stepId) => {
    setBusy('audit');
    try {
      const a = await api.get('/admin/public-launch-audit');
      const verdict = `${a.data.verdict} / ${a.data.announce_verdict} — ${a.data.blockers} blockers / ${a.data.warnings} warnings`;
      const green = a.data.blockers === 0 && a.data.warnings === 0;
      const r = await api.put(`/admin/launch-rehearsal/step/${stepId}`, { status: green ? 'reviewed' : 'pending', note: verdict });
      setData(r.data);
      (green ? toast.success : toast.warning)(`Audit: ${verdict}`);
    } catch { toast.error('Audit failed to run'); }
    setBusy('');
  };

  const finish = async () => {
    setBusy('finish');
    const r = await call(() => api.post('/admin/launch-rehearsal/finish'), 'Rehearsal finished — summary below. Nothing was published.');
    if (r) { setData(r.data); setSummary(r.data.summary); setHistoryKey((k) => k + 1); }
    setBusy('');
  };

  const reset = async () => {
    if (!window.confirm('Reset the rehearsal? All step progress and notes are cleared (the last rehearsal date is kept).')) return;
    setBusy('reset'); setSummary(null);
    const r = await call(() => api.post('/admin/launch-rehearsal/reset'), 'Rehearsal reset');
    if (r) setData(r.data);
    setBusy('');
  };

  const inProgress = data.status === 'in-progress';
  const nextPending = data.steps.find((s) => s.status === 'pending');

  return (
    <div data-testid="admin-rehearsal-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h1 className="font-serif font-black text-2xl text-[#1a2744]">Launch Rehearsal</h1>
        <Button asChild variant="outline" size="sm" className="font-bold"><Link to="/admin/launch-content" data-testid="rehearsal-link-launch-content"><PackageCheck className="h-4 w-4 mr-1" /> Launch Content Setup</Link></Button>
      </div>
      <p className="text-slate-500 text-sm mb-4">A complete launch-day dry run. Walk each step, mark it reviewed, add notes — then finish for a summary.</p>

      <div className="rounded-xl bg-amber-50 border border-amber-200 p-3.5 text-sm text-amber-800 mb-4 flex items-start gap-2" data-testid="rehearsal-dryrun-note">
        <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" />
        <span><strong>Dry run only.</strong> {data.dry_run_note}</span>
      </div>

      {/* Status + controls */}
      <Card className="mb-4" data-testid="rehearsal-status-card">
        <CardContent className="p-4 sm:p-5">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
            <p data-testid="rehearsal-status"><span className="text-slate-400 font-bold text-xs uppercase mr-1.5">Status</span><strong className="text-[#1a2744] capitalize">{data.status.replace('-', ' ')}</strong></p>
            <p data-testid="rehearsal-counts"><span className="text-slate-400 font-bold text-xs uppercase mr-1.5">Progress</span><strong className="text-emerald-700">{data.reviewed_count} reviewed</strong> · <strong className="text-amber-700">{data.skipped_count} skipped</strong> · <strong className="text-slate-500">{data.pending_count} pending</strong> of {data.total_steps}</p>
            <p data-testid="rehearsal-last-date"><span className="text-slate-400 font-bold text-xs uppercase mr-1.5">Last rehearsal</span><strong className="text-[#1a2744]">{fmtDate(data.last_finished_at)}</strong></p>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            <Button onClick={start} disabled={busy === 'start'} className="h-11 bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="rehearsal-start-btn">
              {busy === 'start' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <PlayCircle className="h-4 w-4 mr-1.5" />} {inProgress ? 'Restart Rehearsal' : 'Start Rehearsal'}
            </Button>
            {inProgress && nextPending && (
              <Button onClick={() => document.querySelector(`[data-testid="rehearsal-step-${nextPending.id}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })} variant="outline" className="h-11 font-bold btn-press" data-testid="rehearsal-continue-btn">
                Continue → {nextPending.label}
              </Button>
            )}
            {inProgress && (
              <Button onClick={finish} disabled={busy === 'finish'} variant="outline" className="h-11 font-bold btn-press border-emerald-300 text-emerald-700" data-testid="rehearsal-finish-btn">
                {busy === 'finish' ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Flag className="h-4 w-4 mr-1.5" />} Finish Rehearsal
              </Button>
            )}
            <Button onClick={reset} disabled={busy === 'reset'} variant="outline" className="h-11 font-bold btn-press text-slate-500" data-testid="rehearsal-reset-btn">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset Rehearsal
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Warnings */}
      {data.warnings.length > 0 && (
        <div className="space-y-2 mb-4" data-testid="rehearsal-warnings">
          {data.warnings.map((w, i) => (
            <div key={i} className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-2.5 text-xs text-amber-800" data-testid="rehearsal-warning">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" /> {w}
            </div>
          ))}
        </div>
      )}

      {/* Finish summary */}
      {(summary || data.status === 'finished') && (
        <Card className="mb-4 border-emerald-300 bg-emerald-50/50" data-testid="rehearsal-summary">
          <CardContent className="p-4 sm:p-5">
            <p className="font-serif font-bold text-[#1a2744] flex items-center gap-2"><Rocket className="h-4 w-4 text-[#C9A84C]" /> Rehearsal complete</p>
            <p className="text-sm text-slate-600 mt-1" data-testid="rehearsal-summary-counts">
              {data.reviewed_count} steps reviewed · {data.skipped_count} skipped · {data.pending_count} left pending · finished {fmtDate(data.finished_at)}
            </p>
            <p className="text-xs text-slate-500 mt-1.5">Nothing was published, posted, emailed, or changed — this was a dry run. Fix any warnings above before the real launch day.</p>
          </CardContent>
        </Card>
      )}

      {/* Steps */}
      <div className="space-y-3">
        {data.steps.map((s, idx) => (
          <Card key={s.id} className={s.status === 'reviewed' ? 'border-emerald-200' : ''} data-testid={`rehearsal-step-${s.id}`}>
            <CardContent className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="flex items-start gap-3 min-w-0 flex-1">
                  <span className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-black shrink-0 ${s.status === 'reviewed' ? 'bg-emerald-600 text-white' : 'bg-[#1a2744] text-[#C9A84C]'}`}>{idx + 1}</span>
                  <div className="min-w-0">
                    <p className="font-bold text-[#1a2744] text-sm">{s.label}</p>
                    <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">{s.desc}</p>
                  </div>
                </div>
                <StatusBadge status={s.status} />
              </div>
              <div className="flex flex-wrap gap-1.5 mt-3 sm:pl-10">
                {s.auto_audit ? (
                  <Button onClick={() => runAudit(s.id)} disabled={busy === 'audit'} size="sm" className="h-9 text-xs bg-[#1a2744] hover:bg-[#243456] text-white font-bold btn-press" data-testid="rehearsal-run-audit-btn">
                    {busy === 'audit' ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Rocket className="h-3 w-3 mr-1" />} Run Audit Now
                  </Button>
                ) : null}
                {s.public ? (
                  <Button asChild variant="outline" size="sm" className="h-9 text-xs font-bold" data-testid={`rehearsal-step-open-${s.id}`}>
                    <a href={s.link} target="_blank" rel="noopener noreferrer"><ExternalLink className="h-3 w-3 mr-1" /> {s.link_label}</a>
                  </Button>
                ) : (
                  <Button asChild variant="outline" size="sm" className="h-9 text-xs font-bold" data-testid={`rehearsal-step-open-${s.id}`}>
                    <Link to={s.link}><ExternalLink className="h-3 w-3 mr-1" /> {s.link_label}</Link>
                  </Button>
                )}
                {s.status !== 'reviewed' && (
                  <Button onClick={() => setStep(s.id, 'reviewed')} size="sm" variant="outline" className="h-9 text-xs font-bold border-emerald-300 text-emerald-700" data-testid={`rehearsal-step-review-${s.id}`}>
                    <CheckCircle2 className="h-3 w-3 mr-1" /> Mark Step Reviewed
                  </Button>
                )}
                {s.status === 'pending' && (
                  <Button onClick={() => setStep(s.id, 'skipped')} size="sm" variant="outline" className="h-9 text-xs font-bold text-slate-500" data-testid={`rehearsal-step-skip-${s.id}`}>
                    <SkipForward className="h-3 w-3 mr-1" /> Skip
                  </Button>
                )}
                {s.status !== 'pending' && (
                  <Button onClick={() => setStep(s.id, 'pending')} size="sm" variant="outline" className="h-9 text-xs font-bold text-slate-400" data-testid={`rehearsal-step-undo-${s.id}`}>
                    <RotateCcw className="h-3 w-3 mr-1" /> Undo
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-1.5 mt-2 sm:pl-10">
                <StickyNote className="h-3.5 w-3.5 text-slate-300 shrink-0" />
                <Input defaultValue={s.note || ''} placeholder="Add Note…" onBlur={(e) => { if (e.target.value !== (s.note || '')) saveNote(s.id, e.target.value); }} className="h-9 text-xs" data-testid={`rehearsal-step-note-${s.id}`} />
              </div>
              {s.at && <p className="text-[11px] text-slate-400 mt-1.5 sm:pl-10">Last updated {fmtDate(s.at)}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Phase 51: Rehearsal History Log */}
      <RehearsalHistory refreshKey={historyKey} />
    </div>
  );
}
